import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { format, parseISO } from "date-fns";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import Select from "components/common/Select";
import Text from "components/common/Text";
import Button from "components/common/Button";
import DragAndDrop from "components/common/DragAndDrop";
import DatePicker from "components/common/DatePicker";
import FinanceAdjustmentFields from "components/common/UpdatePaymentStatusModal/FinanceAdjustmentFields";
import { getPayrollDocumentSignedUrl, uploadPayrollDocumentToS3 } from "services/Admin/Customer";
import { PAYMENT_METHOD_OPTIONS } from "constants/paymentMethods";
import { formatFinanceAdjustmentValue, getFinanceAdjustmentAmount } from "util/financeAdjustment";

import "./UpdatePaymentStatusModal.scss";

const getStatusOptions = (currentStatus) => {
  if (currentStatus === "draft") {
    return [
      { value: "processing", label: "Processing" },
      { value: "paid", label: "Paid" },
      { value: "failed", label: "Failed" },
    ];
  }

  if (currentStatus === "processing") {
    return [
      { value: "paid", label: "Paid" },
      { value: "failed", label: "Failed" },
    ];
  }

  // [PRD-3741] A failed payment is not a dead end: finance can record it again.
  if (currentStatus === "failed") {
    return [
      { value: "processing", label: "Processing" },
      { value: "paid", label: "Paid" },
    ];
  }

  return [];
};

// [PRD-3746] Stored dates are calendar dates ("2026-10-01"); new Date() reads those as
// UTC midnight, which is the previous day west of UTC. parseISO reads them as local.
const toLocalDate = (value) => (typeof value === "string" ? parseISO(value) : new Date(value));

const ADJUSTMENT_DEFAULTS = {
  adjustInvoice: false,
  adjustmentNewTotal: "",
  adjustmentReason: "",
};

const getValidationSchema = (currentTotal) => {
  const whenAdjusting = (thenSchema) => ({ is: true, then: thenSchema, otherwise: (schema) => schema.notRequired() });

  return yup.object().shape({
    paymentMethod: yup.string().required("Payment method is required"),
    status: yup.string().required("Status is required"),
    paymentDate: yup.date().required("Payment date is required").nullable(),
    paymentReference: yup.string().when(["paymentMethod", "status"], {
      is: (method, paymentStatus) => method === "wire" && paymentStatus === "paid",
      then: (schema) => schema.required("Payment reference is required when status is paid"),
      otherwise: (schema) => schema.notRequired(),
    }),
    proofOfPayment: yup.mixed().when(["paymentMethod"], {
      is: (method) => method === "cheque",
      then: (schema) => schema.required("Proof of payment is required for cheque payments"),
      otherwise: (schema) => schema.notRequired(),
    }),
    notes: yup.string().notRequired(),
    adjustInvoice: yup.boolean(),
    adjustmentNewTotal: yup
      .number()
      .transform((value, original) => (original === "" || original === null ? undefined : value))
      .when(
        "adjustInvoice",
        whenAdjusting((schema) =>
          schema
            .typeError("New total is required")
            .required("New total is required")
            .positive("New total must be greater than $0")
            .test("cents", "New total can have at most 2 decimal places", (value) =>
              Number.isInteger(Math.round(value * 100 * 1e6) / 1e6)
            )
            .test(
              "differs",
              "New total must be different from the current total",
              (value) => getFinanceAdjustmentAmount(value, currentTotal) !== 0
            )
        )
      ),
    adjustmentReason: yup.string().when(
      "adjustInvoice",
      whenAdjusting((schema) => schema.trim().max(500, "Reason can have at most 500 characters"))
    ),
  });
};

function UpdatePaymentStatusModal({
  open,
  onClose,
  onSubmit,
  paymentData = {},
  paymentHistoryData = {},
  isLoading = false,
  customerId: customerIdProp,
}) {
  const [fileObjects, setFileObjects] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);

  const {
    status = "",
    paymentDate = "",
    paymentMethod: initialPaymentMethod = "",
    customerId: customerIdFromData = "",
    customerPayrollId = "",
  } = paymentData;

  const customerId = customerIdProp || customerIdFromData;

  const {
    billingPeriod: historyBillingPeriod = "",
    paymentType: historyPaymentType = "",
    total: historyTotal = "",
  } = paymentHistoryData || {};

  const total = historyTotal || parseFloat(paymentData.totalPayroll || 0) + parseFloat(paymentData.totalBonus || 0);
  const financeAdjustment = paymentData?.financeAdjustment || null;
  // [PRD-3599] amountDue is the invoice PDF bottom line (after credit and surcharge); "New total" is diffed against it.
  const hasAmountDue = typeof paymentData?.amountDue === "number";
  const currentTotal = hasAmountDue ? paymentData.amountDue : Number(total) || 0;

  const billingPeriod = historyBillingPeriod
    ? new Date(`${historyBillingPeriod}-01`).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : "";
  const paymentType = historyPaymentType || "Regular";

  let currentStatus = status;
  if (status === "un-paid") {
    currentStatus = "draft";
  } else if (status === "finalized") {
    // A finalized invoice is already "in processing" — the dropdown should only
    // offer Paid/Failed (no "Processing"), and it displays as "Processing"
    // (matching the payment-history chip).
    currentStatus = "processing";
  } else if (status === "payment_failed") {
    currentStatus = "failed";
  } else if (status === "pending") {
    currentStatus = "processing";
  }

  const bsInvoiceStatus = paymentHistoryData?.status?.toLowerCase();
  if (bsInvoiceStatus === "finalized" && getStatusOptions(currentStatus).length === 0) {
    currentStatus = "processing";
  }

  const availableStatusOptions = getStatusOptions(currentStatus);

  // The payment method + date + reference from a recorded processing stage must
  // carry over to the paid/failed step and stay read-only. This lock applies
  // ONLY once a processing stage has actually been recorded — a finalized invoice
  // being paid for the FIRST time has no recorded stage yet, so its fields stay
  // editable. (Do NOT key this off currentStatus, which is 'processing' for a
  // plain finalized invoice too.)
  const manualPaymentHistory = Array.isArray(paymentData?.manualPaymentHistory)
    ? paymentData.manualPaymentHistory
    : [];
  // A failed entry closes the attempt before it: only stages after the most
  // recent failure lock the fields, so a new attempt after a bounce starts open.
  const lastFailedIndex = manualPaymentHistory.findLastIndex(
    (stage) => String(stage?.status || "").toLowerCase() === "failed"
  );
  const processingStage = manualPaymentHistory.slice(lastFailedIndex + 1).find((stage) =>
    ["processing", "pending"].includes(String(stage?.status || "").toLowerCase())
  );
  const isMethodLocked = !!processingStage;
  const isProcessingDataLocked = !!processingStage;
  const canAdjustInvoice = currentStatus === "draft" && !financeAdjustment;

  const methods = useForm({
    mode: "all",
    defaultValues: {
      paymentMethod: initialPaymentMethod || "wire",
      status: "",
      paymentDate: processingStage?.paymentDate
        ? toLocalDate(processingStage.paymentDate)
        : paymentDate
          ? toLocalDate(paymentDate)
          : new Date(),
      paymentReference: processingStage?.referenceNumber || "",
      notes: "",
      proofOfPayment: null,
      ...ADJUSTMENT_DEFAULTS,
    },
    resolver: yupResolver(getValidationSchema(currentTotal)),
  });

  const { handleSubmit, formState, watch, reset, setValue } = methods;
  const { isValid, errors } = formState;
  const selectedPaymentMethod = watch("paymentMethod");
  const selectedStatus = watch("status");
  const [adjustInvoice, adjustmentNewTotal] = watch(["adjustInvoice", "adjustmentNewTotal"]);
  const isAdjusting = canAdjustInvoice && adjustInvoice;
  const newTotalValue = Number(adjustmentNewTotal);
  const signedAdjustment =
    adjustmentNewTotal !== "" && newTotalValue > 0 && !errors.adjustmentNewTotal
      ? getFinanceAdjustmentAmount(newTotalValue, currentTotal)
      : null;

  useEffect(() => {
    if (!selectedStatus && availableStatusOptions.length > 0) {
      setValue("status", availableStatusOptions[0].value, { shouldValidate: true });
    } else if (selectedStatus && !availableStatusOptions.find((opt) => opt.value === selectedStatus)) {
      setValue("status", availableStatusOptions[0]?.value || "", { shouldValidate: true });
    }
  }, [currentStatus, selectedStatus, setValue]);

  // Re-initialize the form every time the modal opens. Without this, reopening
  // the modal without navigating away keeps stale form state (e.g. after
  // recording a "processing" stage), leaving isValid/status unresolved and the
  // Save button disabled until a full remount. Reset restores a fresh, valid state.
  useEffect(() => {
    if (open) {
      reset({
        paymentMethod: initialPaymentMethod || "wire",
        status: "",
        paymentDate: processingStage?.paymentDate
          ? toLocalDate(processingStage.paymentDate)
          : paymentDate
            ? toLocalDate(paymentDate)
            : new Date(),
        paymentReference: processingStage?.referenceNumber || "",
        notes: "",
        proofOfPayment: null,
        ...ADJUSTMENT_DEFAULTS,
      });
      setFileObjects([]);
      setUploadedUrls([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    reset();
    setFileObjects([]);
    setUploadedUrls([]);
    setValue("proofOfPayment", null, { shouldValidate: false });
    onClose();
  };

  useEffect(() => {
    if (fileObjects.length < uploadedUrls.length) {
      const updatedUrls = uploadedUrls.filter((_, index) => index < fileObjects.length);
      setUploadedUrls(updatedUrls);
      setValue("proofOfPayment", updatedUrls.length > 0 ? updatedUrls : null, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileObjects.length]);

  const handleFormSubmit = async (data) => {
    const mapStatus = (statusValue) => {
      if (statusValue === "processing") return "pending";
      return statusValue;
    };

    const payload = {
      paymentMethod: data.paymentMethod,
      status: mapStatus(data.status),
      note: data.notes || "",
      referenceNumber: data.paymentReference || "",
      paymentDate: format(data.paymentDate || new Date(), "yyyy-MM-dd"),
      documents: uploadedUrls,
    };

    if (isAdjusting) {
      const reason = (data.adjustmentReason || "").trim();
      payload.adjustment = {
        amount: getFinanceAdjustmentAmount(Number(data.adjustmentNewTotal), currentTotal),
        ...(reason && { reason }),
      };
    }

    // onSubmit resolves false when the save failed; keep the modal open with the typed values.
    if ((await onSubmit(payload)) !== false) handleClose();
  };

  const handleFileUpload = async (fileArray) => {
    if (!Array.isArray(fileArray) || fileArray.length === 0) return;

    await fileArray.reduce(async (previousPromise, fileObj) => {
      await previousPromise;
      try {
        const { file, id } = fileObj;
        const timestamp = Date.now();
        const s3Key = `${timestamp}-${file.name}`;

        const signedUrl = await getPayrollDocumentSignedUrl(customerId, customerPayrollId, s3Key, file.type, file.size);

        await uploadPayrollDocumentToS3(signedUrl, file);

        const s3Url = signedUrl.split("?")[0];

        setFileObjects((prevFiles) =>
          prevFiles.map((f) =>
            f.id === id
              ? {
                  ...f,
                  status: "complete",
                  progress: 100,
                }
              : f
          )
        );

        const documentInfo = {
          displayName: file.name,
          s3Key,
          size: file.size,
          path: s3Url,
          contentType: file.type,
        };

        setUploadedUrls((prev) => {
          const updated = [...prev, documentInfo];
          setValue("proofOfPayment", updated, { shouldValidate: true });
          return updated;
        });
      } catch (error) {
        setFileObjects((prevFiles) =>
          prevFiles.map((f) =>
            f.id === fileObj.id
              ? {
                  ...f,
                  status: "failed",
                  error: "Upload failed",
                  retry: true,
                }
              : f
          )
        );
      }
    }, Promise.resolve());
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatStatusText = (statusValue) => {
    if (!statusValue) return "";
    return statusValue
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getStatusClass = (statusValue) => {
    const statusClasses = {
      draft: "status-draft",
      pending: "status-pending",
      processing: "status-processing",
      paid: "status-paid",
      failed: "status-failed",
    };
    return statusClasses[statusValue] || "status-draft";
  };

  const showPaymentReference = selectedPaymentMethod === "wire";
  const showProofOfPayment = selectedPaymentMethod === "cheque";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="update-payment-status-modal"
      data-testid="update-payment-status-modal"
      size="sm"
      title={<DialogTitle title="Update Payment Status" />}
      content={
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <DialogContent>
              <div className="payment-summary-card">
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">Billing period</p>
                  <p className="paragraph text-lg font-medium line-height-xl payment-summary-value">{billingPeriod}</p>
                </div>
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">Payment type</p>
                  <p className="paragraph text-lg font-medium line-height-xl payment-summary-value">{paymentType}</p>
                </div>
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">
                    {hasAmountDue ? "Amount due" : "Total"}
                  </p>
                  <p className="paragraph text-lg font-medium line-height-xl payment-summary-value">
                    {formatCurrency(hasAmountDue ? paymentData.amountDue : total)}
                  </p>
                </div>
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">Current status</p>
                  <p className={`paragraph text-lg font-medium line-height-xl ${getStatusClass(currentStatus)}`}>
                    {formatStatusText(currentStatus)}
                  </p>
                </div>
              </div>

              {financeAdjustment && (
                <p className="para-body-m-regular finance-adjustment-note">
                  {`Billing adjustment: ${formatFinanceAdjustmentValue(financeAdjustment)}`}
                </p>
              )}

              <div className="field-row">
                <div className="field-col">
                  <Select
                    label="Payment method"
                    options={PAYMENT_METHOD_OPTIONS}
                    name="paymentMethod"
                    placeholder="Select payment method"
                    size="small"
                    disabled={isMethodLocked}
                  />
                </div>
                <div className="field-col">
                  <Select
                    label="Change status to"
                    options={availableStatusOptions}
                    name="status"
                    placeholder="Select status"
                    size="small"
                    disabled={availableStatusOptions.length === 0}
                  />
                </div>
              </div>

              <div className="field-margin-bottom">
                <DatePicker
                  label="Payment date"
                  name="paymentDate"
                  placeholder="Select date"
                  size="medium"
                  disabled={isProcessingDataLocked}
                />
              </div>

              {/* Payment Reference (Wire Transfer only) */}
              {showPaymentReference && (
                <div className="field-margin-bottom">
                  <Text
                    name="paymentReference"
                    label="Payment reference"
                    placeholder="Enter payment reference"
                    size="small"
                    disabled={isProcessingDataLocked && !!processingStage?.referenceNumber}
                  />
                </div>
              )}

              {showProofOfPayment && (
                <div className="field-margin-bottom">
                  <p className="file-upload-label">Proof of payment</p>
                  <DragAndDrop
                    name="proofOfPayment"
                    supportedFormats={["application/pdf", "image/png", "image/jpeg", "image/jpg"]}
                    maxFileSize={3 * 1024 * 1024}
                    helperText="PDF, PNG, or JPG (max. 3MB). Max 2 documents allowed."
                    isMultiFileSupported
                    maximumFilesCount={2}
                    hideWhenMaxFilesReached
                    onFileSelect={handleFileUpload}
                    files={fileObjects}
                    setFiles={setFileObjects}
                  />
                </div>
              )}

              {canAdjustInvoice && (
                <FinanceAdjustmentFields
                  adjustInvoice={!!adjustInvoice}
                  onToggle={(checked) => setValue("adjustInvoice", checked, { shouldValidate: true })}
                  adjustmentAmount={signedAdjustment}
                />
              )}

              {/* Notes */}
              <div>
                <Text
                  name="notes"
                  multiline
                  rows={4}
                  label="Note"
                  isOptional
                  size="medium"
                  placeholder="Enter your note here..."
                />
              </div>
            </DialogContent>
          </form>
        </FormProvider>
      }
      actions={
        <DialogActions
          action1={<Button isDisabled={isLoading} variant="text" label="Cancel" size="large" onClick={handleClose} />}
          action2={
            <Button
              isLoading={isLoading}
              data-testid="submit-payment-status-button"
              onClick={handleSubmit(handleFormSubmit)}
              label={`Mark as ${formatStatusText(selectedStatus)}`}
              size="large"
              isDisabled={!isValid || !selectedStatus}
            />
          }
        />
      }
    />
  );
}

UpdatePaymentStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  customerId: PropTypes.string,
  paymentData: PropTypes.shape({
    status: PropTypes.string,
    paymentDate: PropTypes.string,
    totalPayroll: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalBonus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentMethod: PropTypes.string,
    customerId: PropTypes.string,
    customerPayrollId: PropTypes.string,
    amountDue: PropTypes.number,
    financeAdjustment: PropTypes.shape({
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      reason: PropTypes.string,
      adjustedBy: PropTypes.string,
      adjustedAt: PropTypes.string,
    }),
  }),
  paymentHistoryData: PropTypes.shape({
    billingPeriod: PropTypes.string,
    paymentType: PropTypes.string,
    total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
};

UpdatePaymentStatusModal.defaultProps = {
  paymentData: {
    status: "draft",
    paymentDate: "",
    totalPayroll: 0,
    totalBonus: 0,
    paymentMethod: "",
    customerId: "",
    customerPayrollId: "",
  },
  paymentHistoryData: {
    billingPeriod: "",
    paymentType: "",
    total: 0,
  },
  isLoading: false,
};

export default UpdatePaymentStatusModal;
